import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import moment from 'moment'
import { Topbar } from '@components/topbar'
import { Footer } from '@components/footer'
import { SEO } from '@components/seo'
import { useStoreActions, useStoreState } from '@stores'
import { CLIENT_CODE, SITE_URL, stripHtml, truncate } from '@constants'

const formatDate = (s?: string): string => {
	if (!s) return ''
	const d = moment(s)
	return d.isValid() ? d.format('DD MMM YYYY') : s
}

export const EventDetailPage = () => {
	const { slug } = useParams<{ slug: string }>()
	const { fetchAllEvent } = useStoreActions(action => action.event)
	const { events } = useStoreState(state => state.event)
	const [activePhoto, setActivePhoto] = useState(0)

	useLayoutEffect(() => {
		window.scrollTo(0, 0)
	}, [slug])

	useEffect(() => {
		if (!events || events.length === 0) {
			fetchAllEvent({
				page: 1,
				limit: 100,
				client_code: CLIENT_CODE,
				status: 'all'
			})
		}
	}, [fetchAllEvent, events])

	const event = useMemo(
		() => events?.find(e => e.slug === slug),
		[events, slug]
	)

	useEffect(() => {
		setActivePhoto(0)
	}, [event?.id])

	const dateLabel = event
		? event.period?.start && event.period?.end
			? `${formatDate(event.period.start)} – ${formatDate(event.period.end)}`
			: formatDate(event?.period?.start || event?.period?.end)
		: ''

	const heroImage =
		event?.photos && event.photos.length > 0
			? event.photos[activePhoto] || event.photos[0]
			: null

	const eventJsonLd = event
		? {
				'@context': 'https://schema.org',
				'@type': 'Event',
				name: event.name,
				url: `${SITE_URL}/event/${event.slug}`,
				startDate: event.period?.start || undefined,
				endDate: event.period?.end || undefined,
				eventStatus: 'https://schema.org/EventScheduled',
				eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
				location: {
					'@type': 'Place',
					name: 'Botani Square Mall',
					address: {
						'@type': 'PostalAddress',
						streetAddress: 'Jl. Raya Pajajaran',
						addressLocality: 'Bogor',
						addressRegion: 'Jawa Barat',
						postalCode: '16127',
						addressCountry: 'ID'
					}
				},
				image:
					event.photos && event.photos.length > 0 ? event.photos[0] : undefined,
				description: truncate(stripHtml(event.description), 500),
				organizer: { '@id': `${SITE_URL}/#organization` }
			}
		: null

	const breadcrumb = event
		? {
				'@context': 'https://schema.org',
				'@type': 'BreadcrumbList',
				itemListElement: [
					{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
					{
						'@type': 'ListItem',
						position: 2,
						name: 'Events',
						item: `${SITE_URL}/event`
					},
					{
						'@type': 'ListItem',
						position: 3,
						name: event.name,
						item: `${SITE_URL}/event/${event.slug}`
					}
				]
			}
		: null

	return (
		<div className="min-h-screen bg-gray-50">
			{event && (
				<SEO
					title={event.name}
					description={truncate(
						stripHtml(event.description) ||
							`${event.name} di Botani Square Mall Bogor. ${dateLabel || ''}`.trim(),
						180
					)}
					path={`/event/${event.slug}`}
					type="event"
					image={
						event.photos && event.photos.length > 0 ? event.photos[0] : undefined
					}
					publishedTime={event.period?.start || undefined}
					jsonLd={[eventJsonLd!, breadcrumb!]}
				/>
			)}
			<Topbar />

			<section className="pt-28 pb-8 sm:pt-32 sm:pb-12">
				<div className="max-w-screen-lg mx-auto px-4 sm:px-6">
					<Link
						to="/event"
						className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
					>
						<span aria-hidden>←</span>
						Back to Events
					</Link>
				</div>
			</section>

			<section className="pb-16 sm:pb-24">
				<div className="max-w-screen-lg mx-auto px-4 sm:px-6">
					{!event ? (
						<div className="bg-white rounded-2xl shadow-sm p-10 text-center text-gray-500">
							Loading event details…
						</div>
					) : (
						<article className="bg-white rounded-2xl shadow-sm overflow-hidden">
							<div className="relative bg-gradient-to-br from-blue-100 to-indigo-100">
								{heroImage ? (
									<img
										src={heroImage}
										alt={event.name}
										className="block w-full h-auto"
									/>
								) : (
									<div className="aspect-[16/9] flex items-center justify-center text-6xl">
										🎉
									</div>
								)}
								{event.event_category?.name && (
									<span className="absolute top-4 left-4 bg-primary text-white text-xs font-medium uppercase tracking-wide px-3 py-1 rounded-full shadow">
										{event.event_category.name}
									</span>
								)}
							</div>

							{event.photos && event.photos.length > 1 && (
								<div className="flex gap-2 px-6 pt-4 overflow-x-auto">
									{event.photos.map((src, i) => (
										<button
											key={`${src}-${i}`}
											onClick={() => setActivePhoto(i)}
											className={`relative w-20 h-14 rounded-md overflow-hidden flex-shrink-0 border-2 transition-colors ${
												i === activePhoto
													? 'border-primary'
													: 'border-transparent hover:border-gray-300'
											}`}
										>
											<img
												src={src}
												alt={`${event.name} ${i + 1}`}
												className="w-full h-full object-cover"
											/>
										</button>
									))}
								</div>
							)}

							<div className="p-6 sm:p-10">
								{dateLabel && (
									<div className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 mb-3">
										<svg
											className="w-4 h-4"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
												clipRule="evenodd"
											/>
										</svg>
										{dateLabel}
									</div>
								)}

								<h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">
									{event.name}
								</h1>

								<div
									className="prose prose-sm sm:prose-base max-w-none text-gray-700 leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
									dangerouslySetInnerHTML={{
										__html: event.description || ''
									}}
								/>

								<div className="mt-10 pt-6 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
									<Link
										to="/event"
										className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:border-primary hover:text-primary transition-colors text-sm font-medium"
									>
										← Back to all events
									</Link>
									<span
										className={`inline-flex items-center text-xs font-medium px-3 py-1 rounded-full ${
											event.active
												? 'bg-green-100 text-green-700'
												: 'bg-gray-100 text-gray-500'
										}`}
									>
										{event.active ? 'Active' : 'Inactive'}
									</span>
								</div>
							</div>
						</article>
					)}
				</div>
			</section>

			<Footer />
		</div>
	)
}
